/**
 * `StringUtil` is a utility object that provides methods for transforming strings.
 *
 * @category DataManipulation
 */
export const StringUtil: StringTransformer = {
  /**
   * @property {Function} toTitleCase - Converts a camelCase or PascalCase string to Title Case.
   *
   * @param str - A camelCase or PascalCase string to be converted.
   * @returns The converted string in Title Case format.
   */
  toTitleCase: (str?: string): string => {
    if (!str) return '';

    const spacedStr = str.replace(/([A-Z])/g, ' $1');
    return spacedStr.trim().replace(/\b\w/g, char => char.toUpperCase());
  },

  /**
   * @property {Function} emailToName - Converts an email address to a name by replacing '@' with '_'.
   *
   * @param email - The email address to be converted.
   * @returns The converted name.
   */
  emailToName: (email: string): string => {
    return email.replace('@', '_');
  },

  /**
   * @property {Function} toFaviconUrl - Converts a URL to a Google favicon URL.
   *
   * @param url - The domain URL to retrieve the favicon for.
   * @returns A URL string pointing to the favicon, or an empty string if the URL is invalid.
   */
  toFaviconUrl: (url?: string): string => {
    return url ? `https://www.google.com/s2/favicons?domain=${url}&sz=24` : '';
  },

  /**
   * @property {Function} toUpperCase - Converts a string to uppercase.
   *
   * @param str - The string to be converted.
   * @returns The converted string in uppercase format.
   */
  toUpperCase: (str: string): string => {
    return str.toUpperCase();
  },

  /**
   * @property {Function} extractDomain - Extracts the domain from a URL.
   *
   * @param url - The URL to extract the domain from.
   * @returns The domain extracted from the URL.
   */
  extractDomain: (url: string): string => {
    return url.split('/')[2];
  },
};

/**
 * `StringTransformer` defines methods for transforming strings.
 *
 * @remarks
 * - Use this utility for transforming strings into different formats.
 *
 * @category DataManipulation
 */
export type StringTransformer = {
  /**
   * Converts a camelCase or PascalCase string to Title Case.
   *
   * @param str - A camelCase or PascalCase string to be converted.
   * @returns The converted string in Title Case format.
   */
  toTitleCase: (str?: string) => string;

  /**
   * Converts an email address to a name.
   *
   * @param email - The email address to be converted.
   * @returns The converted name.
   */
  emailToName: (email: string) => string;

  /**
   * Converts a URL to a favicon URL.
   *
   * @param url - The domain URL to retrieve the favicon for.
   * @returns A URL string pointing to the favicon, or an empty string if the URL is invalid.
   */
  toFaviconUrl: (url?: string) => string;

  /**
   * Converts a string to uppercase.
   *
   * @param str - The string to be converted.
   * @returns The converted string in uppercase format.
   */
  toUpperCase: (str: string) => string;

  /**
   * Extracts the domain from a URL.
   *
   * @param url - The URL to extract the domain from.
   * @returns The domain extracted from the URL.
   */
  extractDomain: (url: string) => string;
};
